import { Order } from "../types/AppTypes";
import { useState, useCallback, useEffect, useRef } from "react";
import { useOrders } from "./useOrders";

const useCustomPaginatedOrders = ({
  limit = 9,
  infoVariablesP,
  skip,
  setOrdersLoading,
  dispatch,
  setLocalLoading,
}: {
  limit?: number;
  infoVariablesP?: any;
  skip?: boolean;
  setOrdersLoading?: (loading: boolean) => void;
  dispatch?: any;
  setLocalLoading?: (loading: boolean) => void;
}) => {
  const [ordersForDisplay, setOrdersForDisplay] = useState<Order[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleLoadingChange = useCallback(
    (loading: boolean) => {
      if (setOrdersLoading && dispatch) {
        dispatch(setOrdersLoading(loading));
      }
      if (setLocalLoading) {
        setLocalLoading(loading);
      }
    },
    [setOrdersLoading, dispatch, setLocalLoading]
  );

  const ordersData = useOrders({
    limit,
    infoVariablesP,
    abortControllerRef,
  });

  const loading = ordersData?.loading;

  const loadPage = useCallback(
    async (token: string | null = null) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      try {
        handleLoadingChange(true);
        const ordersResult = await ordersData?.loadPage(
          token,
          abortControllerRef.current.signal
        );

        if (abortControllerRef.current.signal.aborted) return;

        if (!ordersResult?.length) {
          setOrdersForDisplay([]);
          return;
        }

        setOrdersForDisplay(ordersResult);
      } catch (error: any) {
        if (error.name !== "AbortError") {
          console.error("Error loading page:", error);
        }
      } finally {
        handleLoadingChange(false);
        ordersData?.setLoading(false);
      }
    },
    [ordersData?.loadPage]
  );

  useEffect(() => {
    if (!skip) {
      loadPage();
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [skip, loadPage]);

  return {
    orders: ordersData?.orders,
    loading,
    hasNextPage: ordersData?.hasNextPage,
    loadNextPage: async () => {
      await ordersData.loadNextPage();
      await loadPage(ordersData?.nextToken);
    },
    loadPreviousPage: async () => {
      await ordersData?.loadPreviousPage();
      await loadPage();
    },
    loadPage,
    ordersForDisplay,
  };
};

export default useCustomPaginatedOrders;